// ─────────────────────────────────────────────────────────────────────────
// componentes.js — piezas compartidas de "Mi tablero": constantes, paleta,
// estilos de botón y campo, y los bloques chicos (Plazo, Meta, Seccion, Hoja, FilaFrente).
//
// Sin colores literales: todo pasa por las variables --tab-* de src/index.css
// (el CI de colores no admite literales en archivos nuevos).
// ─────────────────────────────────────────────────────────────────────────
import React, { useState } from "react";

export const AREAS = {
  familia: "Caso de familia",
  dinero: "Dinero personal",
  erp: "Binimeliz / ERP",
  nelson: "Proyectos con Nelson y Nicole",
  infra: "Infraestructura y agentes",
  trading: "Trading",
  ninos: "Niños y colegio",
  personal: "Personal",
  reuniones: "Reuniones y actas",
};
export const QUIEN = { maxi: "Maxi", agente: "Agente", tercero: "Tercero" };

export const CERRADOS = ["cerrado", "descartado"];

export const diaDe = f => (f ? new Date(f).toISOString().slice(0, 10) : null);
export const fmtDia = d => (d ? `${d.slice(8, 10)}-${d.slice(5, 7)}` : "");
export const diasEntre = (a, b) => Math.round((Date.parse(b + "T00:00:00Z") - Date.parse(a + "T00:00:00Z")) / 86400000);

export const fechaLarga = hoy => {
  if (!hoy) return "";
  const d = new Date(hoy + "T12:00:00Z");
  return d.toLocaleDateString("es-CL", { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" });
};

// Paleta en variables CSS (src/index.css, bloque "Mi tablero").
export const C = {
  fondo: "var(--tab-fondo)",
  caja: "var(--tab-caja)",
  borde: "var(--tab-borde)",
  texto: "var(--tab-texto)",
  suave: "var(--tab-suave)",
  rojo: "var(--tab-rojo)",
  ambar: "var(--tab-ambar)",
  verde: "var(--tab-verde)",
  azul: "var(--tab-azul)",
  boton: "var(--tab-boton)",
  campo: "var(--tab-campo)",
  toast: "var(--tab-toast)",
  velo: "var(--tab-velo)",
  sombra: "var(--tab-sombra)",
};

export const boton = (color = C.borde, activo = false) => ({
  minHeight: 44,
  padding: "8px 14px",
  borderRadius: 10,
  border: `1px solid ${activo ? color : C.borde}`,
  background: activo ? color : C.boton,
  color: C.texto,
  fontSize: 15,
  cursor: "pointer",
});
export const campo = {
  width: "100%",
  minHeight: 44,
  padding: "8px 10px",
  borderRadius: 10,
  border: `1px solid ${C.borde}`,
  background: C.campo,
  color: C.texto,
  fontSize: 16,
  boxSizing: "border-box",
};

/** Lee/escribe preferencias de la pantalla en localStorage; nunca rompe si el navegador lo bloquea. */
export const leerPref = (clave, porDefecto) => {
  try {
    const v = window.localStorage.getItem(`tablero.${clave}`);
    return v == null ? porDefecto : JSON.parse(v);
  } catch (e) {
    return porDefecto;
  }
};
export const guardarPref = (clave, valor) => {
  try {
    window.localStorage.setItem(`tablero.${clave}`, JSON.stringify(valor));
  } catch (e) {
    // Sin almacenamiento (modo privado o bloqueado): la pantalla funciona igual.
  }
};

/** Plazo con color: en rojo si venció. */
export const Plazo = ({ f, hoy }) => {
  const dia = diaDe(f.plazo);
  if (dia) {
    const d = diasEntre(dia, hoy);
    if (d > 0) return <span style={{ color: C.rojo }}>venció hace {d} d</span>;
    if (d === 0) return <span style={{ color: C.ambar }}>vence hoy</span>;
    return <span style={{ color: C.suave }}>plazo {fmtDia(dia)}</span>;
  }
  return f.plazoTexto ? <span style={{ color: C.suave }}>plazo: {f.plazoTexto}</span> : null;
};

export const Meta = ({ f, hoy }) => (
  <div style={{ fontSize: 14, color: C.suave, marginTop: 6, display: "flex", flexWrap: "wrap", gap: "4px 12px" }}>
    <span>{QUIEN[f.quien] || "Maxi"}{f.quienDetalle ? ` · ${f.quienDetalle}` : ""}</span>
    <Plazo f={f} hoy={hoy} />
    {f.verificar && <span style={{ color: C.ambar }}>verificar</span>}
  </div>
);

/**
 * Sección con título y contador. Si es plegable puede ser controlada desde afuera
 * (abierta + onToggle, para recordar el estado) o manejar su propio estado.
 */
export const Seccion = ({ titulo, n, children, plegable = false, abierta = true, onToggle, id, primera = false }) => {
  const [verLocal, setVerLocal] = useState(abierta);
  const controlada = typeof onToggle === "function";
  const ver = controlada ? abierta : verLocal;
  const alternar = () => (controlada ? onToggle(!ver) : setVerLocal(v => !v));
  return (
    <section id={id} style={{ marginTop: primera ? 0 : 22, scrollMarginTop: 80 }}>
      <div
        role={plegable ? "button" : undefined}
        aria-expanded={plegable ? ver : undefined}
        onClick={plegable ? alternar : undefined}
        style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".07em", color: C.suave, marginBottom: 10, cursor: plegable ? "pointer" : "default", minHeight: 28 }}
      >
        {plegable ? (ver ? "▾ " : "▸ ") : ""}{titulo}{n != null ? ` (${n})` : ""}
      </div>
      {(!plegable || ver) && children}
    </section>
  );
};

/** Copia al portapapeles; si el navegador no lo permite usa un textarea temporal. */
export const copiarTexto = async texto => {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch (e) {
    try {
      const t = document.createElement("textarea");
      t.value = texto;
      t.style.position = "fixed";
      t.style.opacity = "0";
      document.body.appendChild(t);
      t.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(t);
      return ok;
    } catch (e2) {
      return false;
    }
  }
};

/**
 * Hoja inferior (móvil) / diálogo (escritorio, con centrada): el mismo patrón que el
 * formulario de nuevo frente. Se cierra tocando el velo, salvo que cerrarConVelo sea false
 * (formularios, para no perder lo escrito por un toque accidental). Va sobre el menú
 * inferior (zIndex 1070) y el header móvil (1080).
 */
export const Hoja = ({ onCerrar, children, maxWidth = 760, cerrarConVelo = true, centrada = false }) => (
  <div
    onClick={cerrarConVelo ? onCerrar : undefined}
    style={{ position: "fixed", inset: 0, background: C.velo, display: "flex", alignItems: centrada ? "center" : "flex-end", justifyContent: "center", zIndex: 1090, padding: centrada ? 16 : 0 }}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{
        background: C.caja,
        color: C.texto,
        borderRadius: centrada ? 16 : "16px 16px 0 0",
        padding: 16,
        paddingBottom: centrada ? 16 : "calc(16px + env(safe-area-inset-bottom))",
        width: "100%",
        maxWidth,
        maxHeight: "90vh",
        overflowY: "auto",
        boxSizing: "border-box",
      }}
    >
      {children}
    </div>
  </div>
);

/**
 * Fila compacta de un frente: área, título, plazo y la siguiente acción en una línea.
 * Al tocarla se despliega el detalle (siguiente acción completa, quién, última nota).
 * `extra` y `children` (acciones) quedan siempre visibles.
 */
export const FilaFrente = ({ f, hoy, mostrarArea = true, extra, children }) => {
  const [ver, setVer] = useState(false);
  const nota = (f.notas || []).length > 0 ? f.notas[f.notas.length - 1].texto : "";
  return (
    <div style={{ background: C.caja, border: `1px solid ${C.borde}`, borderRadius: 10, padding: "10px 12px", marginBottom: 8 }}>
      <div role="button" aria-expanded={ver} onClick={() => setVer(v => !v)} style={{ cursor: "pointer" }}>
        {mostrarArea && (
          <div style={{ fontSize: 11, color: C.azul, textTransform: "uppercase", letterSpacing: ".06em" }}>{AREAS[f.area] || f.area}</div>
        )}
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 600, lineHeight: 1.3 }}>{f.titulo}</div>
          <div style={{ fontSize: 13, whiteSpace: "nowrap" }}><Plazo f={f} hoy={hoy} /></div>
        </div>
        {f.siguienteAccion && (
          <div className={ver ? undefined : "tab-una-linea"} style={{ fontSize: 14, color: ver ? C.texto : C.suave, marginTop: 4, lineHeight: 1.4 }}>
            {f.siguienteAccion}
          </div>
        )}
      </div>
      {ver && (
        <>
          <Meta f={f} hoy={hoy} />
          {nota && <div style={{ fontSize: 13, color: C.suave, marginTop: 6 }}>Última nota: {nota}</div>}
        </>
      )}
      {extra}
      {children}
    </div>
  );
};
